/**
 * The three access levels the unit actually talks in — and the one place that
 * translates between them and the account roles the engine enforces.
 *
 * `src/core/permissions.ts` answers "may X do Y?" and knows nothing about
 * ranks; `src/types/fleet.ts` holds the six jobs a soldier is filed under and
 * knows nothing about accounts. This module is the bridge between them:
 *
 * - **מנהל על** — בן פרץ. Every screen, the user directory, PIN resets and the
 *   system configuration. One account (`super_admin`).
 * - **מפקדים ובעלי תפקידים** — מ"פ, סמ"פ, פקיד/ה, קצין רכב, רס"פ. They run the
 *   day: vehicle statuses, soldier statuses, company loans and assignments.
 *   They may not reach the user directory, reset anybody's code or touch the
 *   system configuration (`admin`).
 * - **נהגים** — נהג. The board they are on, and the status of the task they
 *   were given. Nothing else is editable to them (`soldier`).
 *
 * The levels are a *reading* of `User.role`, never a second source of truth:
 * {@link accessLevelFor} derives the level from the account, and
 * {@link accountRoleForSoldierRole} is what the management screen offers when a
 * soldier on the roster is given an account. A permission question is still
 * only ever answered by `hasPermission()`.
 */
import { SOLDIER_ROLES, SOLDIER_ROLE_LABELS } from '../types/fleet';
import type { SoldierRole } from '../types/fleet';
import type { User, UserRole } from '../types/user';

/**
 * The three levels, from the widest down.
 *
 * `role_holder` rather than `commander`: the middle level is the five jobs that
 * run the day, and `commander` is a *soldier* job key this codebase retired
 * (see `LEGACY_SOLDIER_ROLES` in `src/core/soldier-roles.ts`). Two namespaces
 * spelling the same word differently is how a migration eventually refiles the
 * wrong thing.
 */
export const ACCESS_LEVELS = ['super_admin', 'role_holder', 'driver'] as const;

export type AccessLevel = (typeof ACCESS_LEVELS)[number];

export const ACCESS_LEVEL_LABELS: Record<AccessLevel, string> = {
  super_admin: 'מנהל על',
  role_holder: 'מפקד / בעל תפקיד',
  driver: 'נהג',
};

/** The sentence the management screen puts under each level. */
export const ACCESS_LEVEL_DESCRIPTIONS: Record<AccessLevel, string> = {
  super_admin:
    'גישה מלאה לכל המסכים, לניהול המשתמשים, לאיפוס קודי כניסה ולהגדרות המערכת.',
  role_holder:
    'עדכון סטטוס רכבים וחיילים, השאלות לפלוגות ושיבוץ משימות. ללא גישה לניהול הרשאות, לאיפוס קודים ולהגדרות המערכת.',
  driver: 'צפייה בלוח המשימות ועדכון סטטוס המשימות ששובצו לו בלבד.',
};

/** Which account role each level is enforced as. */
export const ACCESS_LEVEL_ROLES: Record<AccessLevel, UserRole> = {
  super_admin: 'super_admin',
  role_holder: 'admin',
  driver: 'soldier',
};

/** The reverse reading — the level an account is held at. */
export const ROLE_ACCESS_LEVELS: Record<UserRole, AccessLevel> = {
  super_admin: 'super_admin',
  admin: 'role_holder',
  soldier: 'driver',
};

/**
 * The five jobs that carry a commander's account. Everything else on the
 * roster — the נהג — is a driver, which is why this list is written out rather
 * than derived by exclusion: adding a seventh job to the company must not
 * silently promote whoever holds it.
 */
export const COMMANDER_SOLDIER_ROLES: readonly SoldierRole[] = [
  'company_commander',
  'deputy_commander',
  'clerk',
  'vehicle_officer',
  'sergeant_major',
];

/** The owner's number — the account the unit's מנהל על signs in with. */
export const SUPER_ADMIN_PHONE = '0524063968';

/** The level an account is held at. `null` has no level at all. */
export function accessLevelFor(user: User | null | undefined): AccessLevel | null {
  if (!user) return null;
  return ROLE_ACCESS_LEVELS[user.role] ?? null;
}

/** The Hebrew name of the level an account is held at. */
export function accessLevelLabel(user: User | null | undefined): string {
  const level = accessLevelFor(user);
  return level ? ACCESS_LEVEL_LABELS[level] : '—';
}

/**
 * The account role a soldier's job earns them — what the management screen
 * proposes when בן פרץ opens an account for somebody on the roster.
 *
 * It is a *proposal*, not a rule: the מנהל על may set any role they like on the
 * account afterwards, and this function is never consulted again once the
 * account exists. A job nobody recognises is read as a נהג, exactly as
 * `normalizeSoldierRole()` reads it.
 */
export function accountRoleForSoldierRole(role: string | null | undefined): UserRole {
  const raw = String(role ?? '').trim();
  return COMMANDER_SOLDIER_ROLES.includes(raw as SoldierRole) ? 'admin' : 'soldier';
}

/** True when the job is one of the five that command the day. */
export const isCommanderSoldierRole = (role: string | null | undefined): boolean =>
  COMMANDER_SOLDIER_ROLES.includes(String(role ?? '').trim() as SoldierRole);

/**
 * How an actor is named in the ledger — "אשר אמר (סמ"פ)".
 *
 * The job is what the unit reads a change by, so it is preferred over the
 * account level whenever the actor has a record on the roster. An account with
 * no soldier record behind it (the מנהל על's own, an office account) is named
 * by its level instead, which is the only other thing there is to say about it.
 */
export function describeActorRole(
  user: Pick<User, 'role'> | null | undefined,
  soldierRole?: string | null,
): string {
  const raw = String(soldierRole ?? '').trim();
  if (raw && SOLDIER_ROLES.includes(raw as SoldierRole)) {
    return SOLDIER_ROLE_LABELS[raw as SoldierRole];
  }
  if (!user) return '—';
  return ACCESS_LEVEL_LABELS[ROLE_ACCESS_LEVELS[user.role]] ?? '—';
}
